import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Grid, Tab, Tabs, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, TablePagination, TextField, Select,
  MenuItem, FormControl, InputLabel, Button, IconButton, Tooltip, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  CircularProgress, LinearProgress, Divider, InputAdornment, Card, CardContent,
} from '@mui/material';
import {
  Search as SearchIcon, Send as SendIcon, Upload as UploadIcon,
  Refresh as RefreshIcon, Email as EmailIcon, Quiz as QuizIcon,
  CloudUpload as CloudUploadIcon, Download as DownloadIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from './api';

// Extract a human-readable string from any axios error shape.
// The backend returns errors as either:
//   { success: false, error: { message, userMessage, ... } }
//   { success: false, message: "..." }
function getApiError(err, fallback = 'Something went wrong') {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (data?.error?.userMessage) return data.error.userMessage;
  if (data?.error?.message)     return data.error.message;
  if (typeof data?.error === 'string') return data.error;
  if (data?.message)            return data.message;
  return fallback;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d) => (d ? format(new Date(d), 'dd MMM yyyy, HH:mm') : '—');

const JOB_STATUS_COLOR = {
  PENDING: 'default',
  PROCESSING: 'info',
  COMPLETED: 'success',
  PARTIAL: 'warning',
  FAILED: 'error',
};

// The available quizzes. Keep ids in sync with the backend (constants/quiz.ts)
// and the landing page (QUIZ_QUESTIONS / QUIZ2_QUESTIONS).
const QUIZ_OPTIONS = [
  { id: 'quiz1', label: 'Quiz 1 · Carbs' },
  { id: 'quiz2', label: 'Quiz 2 · Understand food' },
];
const quizShortLabel = (id) =>
  id === 'quiz2' ? 'Quiz 2' : 'Quiz 1'; // legacy rows have no quizId → treat as Quiz 1
const quizColor = (id) => (id === 'quiz2' ? 'secondary' : 'primary');

function StatCard({ label, value, sub, color = 'primary' }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2, height: '100%' }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
        <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>{value ?? '—'}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

// ─── CSV parser (client-side) ─────────────────────────────────────────────────
// Expected columns: email, lang  (header row required)
// Falls back to lang = 'de' when column is missing.

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { rows: [], errors: ['File is empty or has no data rows'] };

  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const emailIdx = headers.findIndex((h) => h === 'email' || h === 'e-mail');
  const langIdx  = headers.findIndex((h) => h === 'lang' || h === 'language' || h === 'sprache');

  if (emailIdx === -1) {
    return { rows: [], errors: ['CSV must have an "email" column header in the first row'] };
  }

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const rows = [];
  const errors = [];
  const seen = new Set();

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map((c) => c.trim());
    const email = (cols[emailIdx] ?? '').toLowerCase().trim();
    const rawLang = langIdx !== -1 ? (cols[langIdx] ?? '').toLowerCase().trim() : 'de';
    const lang = rawLang === 'en' || rawLang === 'english' ? 'en' : 'de';

    if (!email) continue;
    if (!EMAIL_RE.test(email)) { errors.push(`Row ${i + 1}: invalid email "${email}"`); continue; }
    if (seen.has(email)) { errors.push(`Row ${i + 1}: duplicate email "${email}" skipped`); continue; }

    seen.add(email);
    rows.push({ email, lang });
  }

  return { rows, errors };
}

// ─── SINGLE INVITE DIALOG ─────────────────────────────────────────────────────

function SingleInviteDialog({ open, onClose, onSuccess }) {
  const [email, setEmail]   = useState('');
  const [lang, setLang]     = useState('de');
  const [quizId, setQuizId] = useState('quiz1');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const handleSend = async () => {
    if (!email.trim()) { setError('Email is required'); return; }
    setLoading(true);
    setError('');
    try {
      await api.sendQuizInviteSingle(email.trim().toLowerCase(), lang, quizId);
      onSuccess(`${quizShortLabel(quizId)} invitation sent to ${email}`);
      setEmail(''); setLang('de'); setQuizId('quiz1');
      onClose();
    } catch (err) {
      setError(getApiError(err, 'Failed to send invitation'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Send Single Invitation</DialogTitle>
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Email address" type="email" fullWidth
          value={email} onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
        />
        <FormControl fullWidth>
          <InputLabel>Quiz</InputLabel>
          <Select value={quizId} label="Quiz" onChange={(e) => setQuizId(e.target.value)}>
            {QUIZ_OPTIONS.map((q) => (
              <MenuItem key={q.id} value={q.id}>{q.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Language</InputLabel>
          <Select value={lang} label="Language" onChange={(e) => setLang(e.target.value)}>
            <MenuItem value="de">Deutsch (de)</MenuItem>
            <MenuItem value="en">English (en)</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSend} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── BULK IMPORT TAB ──────────────────────────────────────────────────────────

function BulkImportTab({ onSuccess }) {
  const [preview, setPreview]         = useState(null);   // { rows, errors, fileName }
  const [quizId, setQuizId]           = useState('quiz1');
  const [jobId, setJobId]             = useState(null);
  const [job, setJob]                 = useState(null);
  const [sending, setSending]         = useState(false);
  const [parseError, setParseError]   = useState('');
  const [jobs, setJobs]               = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const pollRef                       = useRef(null);
  const fileRef                       = useRef(null);

  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await api.listQuizBulkJobs({ page: 0, limit: 20 });
      setJobs(res.jobs || []);
    } catch { /* silent */ }
    finally { setJobsLoading(false); }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Poll active job every 2 seconds
  useEffect(() => {
    if (!jobId) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.getQuizBulkJob(jobId);
        setJob(res.data);
        setJobs((prev) => prev.map((j) => (j.id === jobId ? res.data : j)));
        if (['COMPLETED', 'FAILED', 'PARTIAL'].includes(res.data.status)) {
          clearInterval(pollRef.current);
        }
      } catch { /* silent */ }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [jobId]);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.csv$/i)) {
      setParseError('Please upload a CSV file. Format: email,lang (header row required)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const { rows, errors } = parseCSV(e.target.result);
      setPreview({ rows, errors, fileName: file.name });
      setParseError('');
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  const handleStart = async () => {
    if (!preview?.rows?.length) return;
    setSending(true);
    try {
      const res = await api.startQuizBulkJob(preview.rows, quizId);
      const newJobId = res.data.jobId;
      setJobId(newJobId);
      setJob({ id: newJobId, status: 'PENDING', quizId, totalEmails: preview.rows.length, processedEmails: 0, successCount: 0, failedCount: 0, skippedCount: 0, progress: 0 });
      setJobs((prev) => [{ id: newJobId, status: 'PENDING', quizId, totalEmails: preview.rows.length, createdAt: new Date().toISOString() }, ...prev]);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = '';
      onSuccess(`${quizShortLabel(quizId)} bulk job started — ${preview.rows.length} emails queued`);
    } catch (err) {
      setParseError(getApiError(err, 'Failed to start bulk job'));
    } finally {
      setSending(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'email,lang\nexample1@email.com,de\nexample2@email.com,en\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'quiz_invite_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* Upload area */}
      <Paper elevation={0} sx={{ border: '2px dashed', borderColor: 'grey.300', borderRadius: 3, p: 4, textAlign: 'center' }}
        onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
        <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
        <Typography variant="h6" gutterBottom>Upload CSV file</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Required columns: <strong>email</strong>, <strong>lang</strong> (en or de)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Invite to quiz</InputLabel>
            <Select value={quizId} label="Invite to quiz" onChange={(e) => setQuizId(e.target.value)}>
              {QUIZ_OPTIONS.map((q) => (
                <MenuItem key={q.id} value={q.id}>{q.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" component="label" startIcon={<UploadIcon />}>
            Choose File
            <input ref={fileRef} hidden type="file" accept=".csv" onChange={(e) => handleFile(e.target.files[0])} />
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
            Download Template
          </Button>
        </Box>
        {parseError && <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>{parseError}</Alert>}
      </Paper>

      {/* Preview */}
      {preview && (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'grey.50' }}>
            <Box>
              <Typography fontWeight="bold">{preview.fileName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {preview.rows.length} valid email(s) found
                {preview.errors.length > 0 && ` · ${preview.errors.length} warning(s)`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" color="error" onClick={() => { setPreview(null); if (fileRef.current) fileRef.current.value = ''; }}>
                Clear
              </Button>
              <Button variant="contained" startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
                onClick={handleStart} disabled={sending || !preview.rows.length}>
                Start Sending ({preview.rows.length})
              </Button>
            </Box>
          </Box>

          {preview.errors.length > 0 && (
            <Box sx={{ p: 2, bgcolor: 'warning.50', borderTop: '1px solid', borderColor: 'warning.200' }}>
              <Typography variant="caption" color="warning.dark" fontWeight="bold">Warnings:</Typography>
              {preview.errors.slice(0, 5).map((e, i) => (
                <Typography key={i} variant="caption" display="block" color="warning.dark">{e}</Typography>
              ))}
              {preview.errors.length > 5 && <Typography variant="caption" color="warning.dark">…and {preview.errors.length - 5} more</Typography>}
            </Box>
          )}

          <TableContainer sx={{ maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Language</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.rows.slice(0, 50).map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell><Chip label={row.lang.toUpperCase()} size="small" variant="outlined" /></TableCell>
                  </TableRow>
                ))}
                {preview.rows.length > 50 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="caption" color="text.secondary">…and {preview.rows.length - 50} more rows</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Active job progress */}
      {job && ['PENDING', 'PROCESSING'].includes(job.status) && (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'info.200', borderRadius: 2, p: 3 }}>
          <Typography fontWeight="bold" gutterBottom>Job in progress…</Typography>
          <LinearProgress variant="determinate" value={job.progress ?? 0} sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {job.processedEmails} / {job.totalEmails} processed · {job.successCount} sent · {job.skippedCount} skipped · {job.failedCount} failed
          </Typography>
        </Paper>
      )}

      {/* Jobs history */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight="bold">Bulk Job History</Typography>
          <IconButton size="small" onClick={loadJobs}><RefreshIcon /></IconButton>
        </Box>
        <Divider />
        {jobsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>
        ) : jobs.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="text.secondary">No bulk jobs yet</Typography></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  {['File', 'Quiz', 'Status', 'Total', 'Sent', 'Skipped', 'Failed', 'Progress', 'Created'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((j) => (
                  <TableRow key={j.id} hover>
                    <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={j.fileName || 'JSON upload'}><span>{j.fileName || 'JSON upload'}</span></Tooltip>
                    </TableCell>
                    <TableCell><Chip label={quizShortLabel(j.quizId)} color={quizColor(j.quizId)} size="small" variant="outlined" /></TableCell>
                    <TableCell><Chip label={j.status} color={JOB_STATUS_COLOR[j.status] || 'default'} size="small" /></TableCell>
                    <TableCell>{j.totalEmails}</TableCell>
                    <TableCell>{j.successCount ?? 0}</TableCell>
                    <TableCell>{j.skippedCount ?? 0}</TableCell>
                    <TableCell>{j.failedCount ?? 0}</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress variant="determinate"
                          value={j.totalEmails > 0 ? Math.round((j.processedEmails / j.totalEmails) * 100) : 0}
                          sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                        <Typography variant="caption">
                          {j.totalEmails > 0 ? Math.round((j.processedEmails / j.totalEmails) * 100) : 0}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(j.createdAt)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function QuizManager() {
  const [tab, setTab]                 = useState(0);
  const [stats, setStats]             = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Global quiz filter ('' = all quizzes). Scopes the stat cards and both tables.
  const [viewQuiz, setViewQuiz]       = useState('');

  // Invitations state
  const [invitations, setInvitations]   = useState([]);
  const [invTotal, setInvTotal]         = useState(0);
  const [invPage, setInvPage]           = useState(0);
  const [invRows, setInvRows]           = useState(25);
  const [invSearch, setInvSearch]       = useState('');
  const [invLang, setInvLang]           = useState('');
  const [invUsed, setInvUsed]           = useState('');
  const [invLoading, setInvLoading]     = useState(false);

  // Results state
  const [results, setResults]           = useState([]);
  const [resTotal, setResTotal]         = useState(0);
  const [resPage, setResPage]           = useState(0);
  const [resRows, setResRows]           = useState(25);
  const [resSearch, setResSearch]       = useState('');
  const [resLoading, setResLoading]     = useState(false);

  // Dialogs / feedback
  const [singleOpen, setSingleOpen]     = useState(false);
  const [snack, setSnack]               = useState({ open: false, msg: '', severity: 'success' });

  const searchTimer = useRef(null);

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  // ── Stats ──────────────────────────────────────────────────────────────────

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.getQuizStats(viewQuiz || undefined);
      setStats(res.data);
    } catch { /* silent */ }
    finally { setStatsLoading(false); }
  }, [viewQuiz]);

  useEffect(() => { loadStats(); }, [loadStats]);

  // ── Invitations ────────────────────────────────────────────────────────────

  const loadInvitations = useCallback(async (overrides = {}) => {
    setInvLoading(true);
    try {
      const params = {
        page: overrides.page ?? invPage,
        limit: overrides.limit ?? invRows,
        search: overrides.search ?? invSearch,
        lang: overrides.lang ?? invLang,
        used: overrides.used ?? invUsed,
        quizId: overrides.quizId ?? viewQuiz,
      };
      if (!params.lang) delete params.lang;
      if (params.used === '') delete params.used;
      if (!params.quizId) delete params.quizId;
      const res = await api.listQuizInvitations(params);
      setInvitations(res.invitations || []);
      setInvTotal(res.pagination?.total || 0);
    } catch { /* silent */ }
    finally { setInvLoading(false); }
  }, [invPage, invRows, invSearch, invLang, invUsed, viewQuiz]);

  useEffect(() => { if (tab === 0) loadInvitations(); }, [tab, invPage, invRows, invLang, invUsed, viewQuiz]); // eslint-disable-line

  // ── Results ────────────────────────────────────────────────────────────────

  const loadResults = useCallback(async (overrides = {}) => {
    setResLoading(true);
    try {
      const params = {
        page: overrides.page ?? resPage,
        limit: overrides.limit ?? resRows,
        search: overrides.search ?? resSearch,
        quizId: overrides.quizId ?? viewQuiz,
      };
      if (!params.quizId) delete params.quizId;
      const res = await api.listQuizResults(params);
      setResults(res.results || []);
      setResTotal(res.pagination?.total || 0);
    } catch { /* silent */ }
    finally { setResLoading(false); }
  }, [resPage, resRows, resSearch, viewQuiz]);

  useEffect(() => { if (tab === 2) loadResults(); }, [tab, resPage, resRows, viewQuiz]); // eslint-disable-line

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Quiz Manager</Typography>
          <Typography variant="body2" color="text.secondary">Manage quiz invitations, bulk imports, and results</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Quiz</InputLabel>
            <Select value={viewQuiz} label="Quiz" onChange={(e) => { setViewQuiz(e.target.value); setInvPage(0); setResPage(0); }}>
              <MenuItem value="">All quizzes</MenuItem>
              {QUIZ_OPTIONS.map((q) => (
                <MenuItem key={q.id} value={q.id}>{q.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { loadStats(); if (tab === 0) loadInvitations(); if (tab === 2) loadResults(); }}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<SendIcon />} onClick={() => setSingleOpen(true)}>
            Send Invitation
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      {statsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>
      ) : stats && (
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Total Invited" value={stats.totalInvited} color="primary" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Quiz Completed" value={stats.totalCompleted} color="success" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Pending" value={stats.pendingInvitations} color="warning" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Conversion" value={`${stats.conversionRate}%`} color="info" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Avg. Score" value={stats.averageScore ?? '—'} color="secondary" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Promo Subscribers" value={stats.quizUsersWithSubscription} color="success"
              sub="6-month plan granted" />
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid', borderColor: 'grey.200', px: 2 }}>
          <Tab label="Invitations" icon={<EmailIcon />} iconPosition="start" />
          <Tab label="Bulk Import" icon={<CloudUploadIcon />} iconPosition="start" />
          <Tab label="Results" icon={<QuizIcon />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 2 }}>

          {/* ── Tab 0: Invitations ─────────────────────────────────────────── */}
          {tab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Filters */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField size="small" placeholder="Search email…" value={invSearch}
                  onChange={(e) => {
                    const val = e.target.value; setInvSearch(val);
                    clearTimeout(searchTimer.current);
                    searchTimer.current = setTimeout(() => { setInvPage(0); loadInvitations({ search: val, page: 0 }); }, 400);
                  }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  sx={{ minWidth: 220 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Language</InputLabel>
                  <Select value={invLang} label="Language"
                    onChange={(e) => { setInvLang(e.target.value); setInvPage(0); }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="de">DE</MenuItem>
                    <MenuItem value="en">EN</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Status</InputLabel>
                  <Select value={invUsed} label="Status"
                    onChange={(e) => { setInvUsed(e.target.value); setInvPage(0); }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="false">Pending</MenuItem>
                    <MenuItem value="true">Used</MenuItem>
                  </Select>
                </FormControl>
                {(invSearch || invLang || invUsed) && (
                  <Button size="small" onClick={() => { setInvSearch(''); setInvLang(''); setInvUsed(''); setInvPage(0); loadInvitations({ search: '', lang: '', used: '', page: 0 }); }}>
                    Clear filters
                  </Button>
                )}
              </Box>

              {/* Table */}
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.100' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      {['Email', 'Quiz', 'Lang', 'Status', 'Sent At', 'Used At'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invLoading ? (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                    ) : invitations.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No invitations found</TableCell></TableRow>
                    ) : invitations.map((inv) => (
                      <TableRow key={inv.id} hover>
                        <TableCell>{inv.email}</TableCell>
                        <TableCell><Chip label={quizShortLabel(inv.quizId)} color={quizColor(inv.quizId)} size="small" variant="outlined" /></TableCell>
                        <TableCell><Chip label={inv.lang.toUpperCase()} size="small" variant="outlined" /></TableCell>
                        <TableCell>
                          <Chip label={inv.usedAt ? 'Used' : 'Pending'}
                            color={inv.usedAt ? 'success' : 'default'} size="small" />
                        </TableCell>
                        <TableCell><Typography variant="caption">{fmtDate(inv.sentAt)}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{inv.usedAt ? fmtDate(inv.usedAt) : '—'}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div" count={invTotal} page={invPage} rowsPerPage={invRows}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={(_, p) => setInvPage(p)}
                onRowsPerPageChange={(e) => { setInvRows(parseInt(e.target.value, 10)); setInvPage(0); }}
              />
            </Box>
          )}

          {/* ── Tab 1: Bulk Import ─────────────────────────────────────────── */}
          {tab === 1 && (
            <BulkImportTab onSuccess={(msg) => { showSnack(msg); loadStats(); }} />
          )}

          {/* ── Tab 2: Results ────────────────────────────────────────────── */}
          {tab === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Filters */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField size="small" placeholder="Search email…" value={resSearch}
                  onChange={(e) => {
                    const val = e.target.value; setResSearch(val);
                    clearTimeout(searchTimer.current);
                    searchTimer.current = setTimeout(() => { setResPage(0); loadResults({ search: val, page: 0 }); }, 400);
                  }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  sx={{ minWidth: 220 }}
                />
                {resSearch && (
                  <Button size="small" onClick={() => { setResSearch(''); setResPage(0); loadResults({ search: '', page: 0 }); }}>
                    Clear
                  </Button>
                )}
              </Box>

              {/* Table */}
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.100' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      {['Email', 'Quiz', 'Score', 'Total Qs', 'Completed At'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resLoading ? (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                    ) : results.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No results yet</TableCell></TableRow>
                    ) : results.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.email}</TableCell>
                        <TableCell><Chip label={quizShortLabel(r.quizId)} color={quizColor(r.quizId)} size="small" variant="outlined" /></TableCell>
                        <TableCell>
                          <Chip label={`${r.score} / ${r.totalQuestions}`}
                            color={r.score / r.totalQuestions >= 0.7 ? 'success' : r.score / r.totalQuestions >= 0.4 ? 'warning' : 'error'}
                            size="small" />
                        </TableCell>
                        <TableCell>{r.totalQuestions}</TableCell>
                        <TableCell><Typography variant="caption">{fmtDate(r.completedAt)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div" count={resTotal} page={resPage} rowsPerPage={resRows}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={(_, p) => setResPage(p)}
                onRowsPerPageChange={(e) => { setResRows(parseInt(e.target.value, 10)); setResPage(0); }}
              />
            </Box>
          )}
        </Box>
      </Paper>

      {/* Single invite dialog */}
      <SingleInviteDialog open={singleOpen} onClose={() => setSingleOpen(false)}
        onSuccess={(msg) => { showSnack(msg); loadStats(); loadInvitations(); }} />

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
